import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Switch,
  FormControlLabel,
  Alert,
  Snackbar,
  Chip
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  LocationOn as LocationIcon
} from '@mui/icons-material';
import { productService } from '../../services/firebase';
import { ServiceArea } from '../../types/models';
import { logger } from '../../services/loggerService';
import MapPicker from '../../components/MapPicker/MapPicker';

const ServiceAreas: React.FC = () => {
  const [serviceAreas, setServiceAreas] = useState<ServiceArea[]>([]);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingArea, setEditingArea] = useState<ServiceArea | null>(null);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error';
  }>({ open: false, message: '', severity: 'success' });

  const [formData, setFormData] = useState({
    name: '',
    centerLat: '',
    centerLng: '',
    radiusKm: '',
    isActive: true
  });

  useEffect(() => {
    loadServiceAreas();
  }, []);

  const loadServiceAreas = async () => {
    setLoading(true);
    try {
      const areas = await productService.getServiceAreas();
      setServiceAreas(areas);
    } catch (error) {
      logger.error('Error loading service areas', error);
      setSnackbar({ open: true, message: 'Failed to load service areas', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (area?: ServiceArea) => {
    if (area) {
      setEditingArea(area);
      setFormData({
        name: area.name,
        centerLat: area.centerLat.toString(),
        centerLng: area.centerLng.toString(),
        radiusKm: area.radiusKm.toString(),
        isActive: area.isActive
      });
    } else {
      setEditingArea(null);
      setFormData({
        name: '',
        centerLat: '',
        centerLng: '',
        radiusKm: '',
        isActive: true
      });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingArea(null);
    setFormData({
      name: '',
      centerLat: '',
      centerLng: '',
      radiusKm: '',
      isActive: true
    });
  };

  const handleSave = async () => {
    try {
      if (!formData.name.trim()) {
        setSnackbar({ open: true, message: 'Area name is required', severity: 'error' });
        return;
      }

      const lat = parseFloat(formData.centerLat);
      const lng = parseFloat(formData.centerLng);
      const radius = parseFloat(formData.radiusKm);

      if (isNaN(lat) || lat < -90 || lat > 90) {
        setSnackbar({ open: true, message: 'Invalid latitude (must be between -90 and 90)', severity: 'error' });
        return;
      }

      if (isNaN(lng) || lng < -180 || lng > 180) {
        setSnackbar({ open: true, message: 'Invalid longitude (must be between -180 and 180)', severity: 'error' });
        return;
      }

      if (isNaN(radius) || radius <= 0) {
        setSnackbar({ open: true, message: 'Radius must be greater than 0', severity: 'error' });
        return;
      }

      const areaData = {
        name: formData.name.trim(),
        centerLat: lat,
        centerLng: lng,
        radiusKm: radius,
        isActive: formData.isActive,
        updatedAt: new Date(),
        ...(editingArea ? {} : { createdAt: new Date() })
      };

      if (editingArea) {
        await productService.updateServiceArea(editingArea.id, areaData);
        setSnackbar({ open: true, message: 'Service area updated successfully', severity: 'success' });
      } else {
        await productService.addServiceArea(areaData as Omit<ServiceArea, 'id'>);
        setSnackbar({ open: true, message: 'Service area added successfully', severity: 'success' });
      }

      handleCloseDialog();
      loadServiceAreas();
    } catch (error) {
      logger.error('Error saving service area', error);
      setSnackbar({ open: true, message: 'Failed to save service area', severity: 'error' });
    }
  };

  const handleDelete = async (id: string, name: string) => {
    if (!window.confirm(`Are you sure you want to delete "${name}"?`)) {
      return;
    }

    try {
      await productService.deleteServiceArea(id);
      setSnackbar({ open: true, message: 'Service area deleted successfully', severity: 'success' });
      loadServiceAreas();
    } catch (error) {
      logger.error('Error deleting service area', error);
      setSnackbar({ open: true, message: 'Failed to delete service area', severity: 'error' });
    }
  };

  const handleToggleActive = async (area: ServiceArea) => {
    try {
      await productService.updateServiceArea(area.id, {
        isActive: !area.isActive,
        updatedAt: new Date()
      });
      setSnackbar({
        open: true,
        message: `Service area ${!area.isActive ? 'activated' : 'deactivated'} successfully`,
        severity: 'success'
      });
      loadServiceAreas();
    } catch (error) {
      logger.error('Error toggling service area status', error);
      setSnackbar({ open: true, message: 'Failed to update service area', severity: 'error' });
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Service Area Configuration
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Define delivery service areas with center coordinates and radius
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
        >
          Add Service Area
        </Button>
      </Box>

      <Alert severity="info" sx={{ mb: 3 }}>
        Orders will only be accepted from locations within these active service areas.
      </Alert>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell><strong>Area Name</strong></TableCell>
              <TableCell><strong>Center Coordinates</strong></TableCell>
              <TableCell><strong>Radius (km)</strong></TableCell>
              <TableCell><strong>Status</strong></TableCell>
              <TableCell align="right"><strong>Actions</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={5} align="center">Loading...</TableCell>
              </TableRow>
            ) : serviceAreas.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  No service areas configured. Click "Add Service Area" to get started.
                </TableCell>
              </TableRow>
            ) : (
              serviceAreas.map((area) => (
                <TableRow key={area.id}>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <LocationIcon color="primary" fontSize="small" />
                      {area.name}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                      {area.centerLat.toFixed(6)}, {area.centerLng.toFixed(6)}
                    </Typography>
                  </TableCell>
                  <TableCell>{area.radiusKm} km</TableCell>
                  <TableCell>
                    <Chip
                      label={area.isActive ? 'Active' : 'Inactive'}
                      color={area.isActive ? 'success' : 'default'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell align="right">
                    <FormControlLabel
                      control={
                        <Switch
                          checked={area.isActive}
                          onChange={() => handleToggleActive(area)}
                          size="small"
                        />
                      }
                      label=""
                      sx={{ mr: 1 }}
                    />
                    <IconButton
                      size="small"
                      color="primary"
                      onClick={() => handleOpenDialog(area)}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => handleDelete(area.id, area.name)}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingArea ? 'Edit Service Area' : 'Add Service Area'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
            <TextField
              label="Area Name"
              fullWidth
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="e.g., Baner, Kharadi"
              helperText="Enter a descriptive name for this service area"
            />

            <MapPicker
              lat={parseFloat(formData.centerLat) || 18.5204}
              lng={parseFloat(formData.centerLng) || 73.8567}
              onLocationSelect={(lat, lng) => {
                setFormData({
                  ...formData,
                  centerLat: lat.toString(),
                  centerLng: lng.toString()
                });
              }}
            />

            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                label="Center Latitude"
                type="number"
                fullWidth
                value={formData.centerLat}
                onChange={(e) => setFormData({ ...formData, centerLat: e.target.value })}
                placeholder="e.g., 18.5642"
                helperText="Latitude (-90 to 90)"
                inputProps={{ step: 'any' }}
              />

              <TextField
                label="Center Longitude"
                type="number"
                fullWidth
                value={formData.centerLng}
                onChange={(e) => setFormData({ ...formData, centerLng: e.target.value })}
                placeholder="e.g., 73.7769"
                helperText="Longitude (-180 to 180)"
                inputProps={{ step: 'any' }}
              />
            </Box>

            <TextField
              label="Radius (km)"
              type="number"
              fullWidth
              value={formData.radiusKm}
              onChange={(e) => setFormData({ ...formData, radiusKm: e.target.value })}
              placeholder="e.g., 3"
              helperText="Service radius in kilometers from the center point"
              inputProps={{ min: 0.1, step: 0.1 }}
            />

            <FormControlLabel
              control={
                <Switch
                  checked={formData.isActive}
                  onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                />
              }
              label="Active"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSave} variant="contained">
            {editingArea ? 'Update' : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ServiceAreas;
